import { Box, Flex, Heading, Input, InputGroup, Spinner, Text } from "@chakra-ui/react";
import { createColumnHelper } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import WelcomeSection from "components/WelcomeSection";
import { ReactTable } from "components/ReactTable";
import { useTrackingTrips } from "hooks/useTrackingTrips";
import type { Trip } from "services/tracking/tripsService";
import { Pagination } from "components/Pagination";

function formatDate(s: string | null | undefined) {
  if (!s) return "—";
  return new Date(s).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
}

export default function TrackingTripsListPage() {
  const { trips, isLoading, error } = useTrackingTrips();
  const [search, setSearch] = useState("");
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize] = useState(25);

  const filtered = useMemo(() => {
    if (!search.trim()) return trips;
    const q = search.trim().toLowerCase();
    return trips.filter(
      (t) =>
        t.bus?.name?.toLowerCase().includes(q) ||
        t.route?.name?.toLowerCase().includes(q) ||
        t.id.toLowerCase().includes(q)
    );
  }, [trips, search]);

  const numberOfPages = useMemo(
    () => Math.max(1, Math.ceil(filtered.length / pageSize)),
    [filtered.length, pageSize]
  );

  const pagedTrips = useMemo(() => {
    const start = (pageIndex - 1) * pageSize;
    const end = start + pageSize;
    return filtered.slice(start, end);
  }, [filtered, pageIndex, pageSize]);

  const handleNextPage = () => {
    setPageIndex((prev) => (prev < numberOfPages ? prev + 1 : prev));
  };

  const handlePrevPage = () => {
    setPageIndex((prev) => (prev > 1 ? prev - 1 : prev));
  };

  const handleJumpToPage = (page: number) => {
    if (!Number.isFinite(page)) return;
    if (page < 1) setPageIndex(1);
    else if (page > numberOfPages) setPageIndex(numberOfPages);
    else setPageIndex(page);
  };

  const col = createColumnHelper<Trip>();
  const columns = useMemo(
    () => [
      col.accessor("id", { header: "ID", cell: (info) => <Text fontSize="xs">{String(info.getValue()).slice(0, 8)}…</Text> }),
      col.accessor((r) => r.bus?.name, { id: "bus", header: "Bus", cell: (info) => <Text fontSize="sm">{info.getValue() ?? "—"}</Text> }),
      col.accessor((r) => r.route?.name, { id: "route", header: "Route", cell: (info) => <Text fontSize="sm">{info.getValue() ?? "—"}</Text> }),
      col.accessor("status", { header: "Statut", cell: (info) => <Text fontSize="sm">{info.getValue()}</Text> }),
      col.accessor("started_at", { header: "Début", cell: (info) => <Text fontSize="sm">{formatDate(info.getValue())}</Text> }),
      col.accessor("created_at", { header: "Créé", cell: (info) => <Text fontSize="sm">{formatDate(info.getValue())}</Text> }),
    ],
    []
  );

  return (
    <>
      <Flex gap="18px" bg="very_light_gray" flex={1} flexDirection="column" py="20px">
        <WelcomeSection />
        <Flex flexDirection="column" alignItems="start" px={{ base: "20px", sm: "24px" }} minHeight="100vh">
          <Heading size="text2xl" as="h5" fontFamily="Arimo" mb={4}>Trajets</Heading>
          {error && (
            <Box mt={4} p={4} bg="red.50" borderRadius="md">
              <Text color="red.700">Erreur chargement.</Text>
            </Box>
          )}
          <Box mt={4} bg="white" w="100%" borderRadius="10px">
            <Flex p={4}>
              <InputGroup w={{ base: "100%", sm: "300px" }}>
                <Input placeholder="Rechercher (bus, route)" value={search} onChange={(e) => setSearch(e.target.value)} />
              </InputGroup>
            </Flex>
            {isLoading && !filtered.length ? (
              <Flex justify="center" py={10}><Spinner size="lg" /></Flex>
            ) : (
              <>
                <ReactTable columns={columns as any} data={pagedTrips} size="sm" />
                {filtered.length > 0 && (
                  <Pagination
                    pageIndex={pageIndex}
                    setNextPageIndex={handleNextPage}
                    setPrevPageIndex={handlePrevPage}
                    jumpToPage={handleJumpToPage}
                    numberOfPages={numberOfPages}
                  />
                )}
              </>
            )}
            {!isLoading && !filtered.length && (
              <Box p={8} textAlign="center"><Text color="gray.600">Aucun trajet.</Text></Box>
            )}
          </Box>
        </Flex>
      </Flex>
    </>
  );
}
